import React from 'react';
import Img from 'gatsby-image';
import { Heading, HeadingElement } from 'components/LANDR/Heading';
import { Container } from '../../UI/Container/Container';
import { PrismicHTMLParser } from '../../PrismicHTMLParser';
import './TextGrid.scss';
import { Grid, GridRow, GridCol, colSize_t, offsetSize_t } from '../../UI/Grid';

type TextGridProps = {
    slice: TextGridSlice;
};

type TextGridTitle = {
    raw: Array<{
        type: keyof typeof HeadingElement;
        text: string;
    }> | null;
} | null;

type TextGridSlice = {
    slice_type: 'text_grid';
    primary: {
        text_grid_number_of_columns: '2' | '3';
        grid_title: TextGridTitle;
    };
    items: TextGridItem[];
};

type TextGridItem = {
    text_grid_title: {
        html: string | null;
    };
    text_grid_description: {
        html: string | null;
    };
    text_grid_logo: {
        url: string | null;
        dimensions: {
            width: number;
            height: number;
        } | null;
    };
};

// Do the conversion in column width
const getColSize = (nbrCol: string, hasTitle: boolean): colSize_t | undefined => {
    switch (nbrCol) {
        case '2':
            return hasTitle ? '4' : '6';
        case '3':
            return hasTitle ? '3' : '4';
        default:
            return;
    }
};

// Returns an offset when there is a title and the element is the first of the 2nd+ row.
// This allows the title have the full height of the grid
const calculateOffset = (
    i: number,
    nbrCol: string,
    hasTitle: boolean,
): offsetSize_t | undefined => {
    if (hasTitle && i !== 0) {
        const modulo: number = i % parseInt(nbrCol, 10);

        if (modulo === 0) {
            return getColSize(nbrCol, hasTitle);
        }
    }
};

const TextGridItem = ({
    item,
    colSize,
    offsetSize,
}: {
    item: TextGridItem;
    colSize: colSize_t | undefined;
    offsetSize: offsetSize_t | undefined;
}) => {
    return (
        <GridCol
            colSize={colSize}
            offsetSize={offsetSize}
            tabletColSize={'6'}
            tabletOffsetSize={'0'}
            mobileColSize={'12'}
            className="Bloc-text"
        >
            <div className="TextGrid-item">
                <div className="TextGrid-item-header">
                    {item.text_grid_logo.dimensions && (
                        <Img
                            className="u-mr-1"
                            fixed={{
                                src: item.text_grid_logo.url,
                                srcSet: '',
                                width: item.text_grid_logo.dimensions.width,
                                height: item.text_grid_logo.dimensions.height,
                            }}
                        />
                    )}
                    <PrismicHTMLParser>{item.text_grid_title.html}</PrismicHTMLParser>
                </div>
                <PrismicHTMLParser className="TextGrid-item-content">
                    {item.text_grid_description.html}
                </PrismicHTMLParser>
            </div>
        </GridCol>
    );
};

const TextGrid = ({ slice }: TextGridProps) => {
    const nbrCol = slice.primary.text_grid_number_of_columns;
    const gridTitle = slice.primary.grid_title && slice.primary.grid_title.raw;
    const hasTitle = !!gridTitle && gridTitle[0].text !== '';
    const colSize = getColSize(nbrCol, hasTitle);
    return (
        <Container size="md">
            <Grid className="Text-bloc">
                <GridRow
                    horizontalAlign={'h-start'}
                    verticalAlign={'v-stretch'}
                    className="Bloc-row"
                >
                    {gridTitle && gridTitle[0].text !== '' ? (
                        <GridCol colSize={colSize} tabletColSize={'12'} className="Col-title">
                            <Heading
                                className="TextGrid-title"
                                size={gridTitle[0].type === 'heading4' ? 'xl' : 'xxl'}
                                element={gridTitle[0].type}
                            >
                                {gridTitle[0].text}
                            </Heading>
                        </GridCol>
                    ) : (
                        ''
                    )}

                    {slice.items.map((item, i: number) => (
                        <TextGridItem
                            key={i}
                            item={item}
                            colSize={colSize}
                            offsetSize={calculateOffset(i, nbrCol, hasTitle)}
                        />
                    ))}
                </GridRow>
            </Grid>
        </Container>
    );
};

export { TextGrid, TextGridProps, TextGridItem };
